<template>
  <div class="shifts">
    <el-card shadow="never">
      <el-table ref="tableRef" :data="props.chatter.assigned_shifts">
        <el-table-column type="expand">
          <template #default="shifts">
            <template v-for="(group, index) of getShiftIncomes(shifts.row)" :key="group.model">
              <el-table
                :data="group.incomes"
                show-summary
                :summary-method="getSummaries"
                :show-header="index === 0"
              >
                <el-table-column label="Модель">
                  <template #default="{ row }">
                    <template v-if="row.is_delayed"> + </template>
                    <template v-else>
                      {{ row.model }}
                    </template>
                  </template>
                </el-table-column>
                <el-table-column label="Сумма" prop="amount" />
                <el-table-column label="Бомж +/-">
                  <template #default="{ row }">
                    <template v-if="!row.is_delayed">
                      {{ row.success_poor_detects }}/{{ row.failed_poor_detects }}
                    </template>
                  </template>
                </el-table-column>
                <el-table-column label="Общаюсь">
                  <template #default="{ row }">
                    <template v-if="!row.is_delayed">
                      {{ row.in_progress }}
                    </template>
                  </template>
                </el-table-column>
                <el-table-column label="Непрочит">
                  <template #default="{ row }">
                    <template v-if="!row.is_delayed">
                      {{ row.unread }}
                    </template>
                  </template>
                </el-table-column>
                <el-table-column label="Коммент" prop="comment" />
                <el-table-column>
                  <template #default="{ row }">
                    <el-icon @click="openedScreenshot = row.id" style="cursor: pointer">
                      <Picture />
                    </el-icon>
                    <el-icon
                      @click="deleteIncome(row.id, shifts.row.id)"
                      style="cursor: pointer; margin-left: 2em"
                    >
                      <Delete />
                    </el-icon>
                  </template>
                </el-table-column>
              </el-table>
            </template>
            <el-divider content-position="left">Еще</el-divider>
            <AddIncome
              isExtended
              :modelValue="income"
              @update:modelValue="
                (income, screenshots) =>
                  addIncome(shifts.row.id, income as IncomeToCreate, screenshots)
              "
            />
          </template>
        </el-table-column>
        <el-table-column label="Тотал">
          <template #default="{ row }">
            {{ row.incomes.reduce((acc: number, income: Income) => acc + income.amount, 0) }}
          </template>
        </el-table-column>
        <el-table-column label="Смена" prop="time" />
        <el-table-column label="Дата">
          <template #default="{ row }">
            {{ formatToHumanReadable(row.date, 'DD.MM') }}
            {{ row.is_intern ? 'Стажер' : '' }}
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    <ShiftAssign :chatter="props.chatter" @change="addIncome" />
  </div>
  <el-dialog :model-value="!!openedScreenshot" @close="openedScreenshot = null">
    <template
      v-if="openedScreenshot"
      v-for="photo of props.chatter.income_photos[openedScreenshot]"
      :key="photo"
    >
      <el-image :src="getPhotoUrl(photo)" class="screenshot">
        <template #error> Нет скриншотов </template>
      </el-image>
    </template>
  </el-dialog>
  <el-dialog :model-value="!!openedDelayed" @close="openedDelayed = null" title="Долет">
    <AddDelayed v-if="openedDelayed" :income="openedDelayed" @change="addIncome" />
  </el-dialog>
</template>

<script setup lang="ts">
import type Chatter from '@/api/resources/chatters/model'
import type Income from '@/api/resources/incomes.ts/model'
import type { IncomeToCreate } from '@/api/resources/incomes.ts/model'
import ShiftAssign from './ShiftAssign.vue'
import { formatToHumanReadable } from '@/utils'
import AddIncome from './AddIncome.vue'
import { h, ref, resolveComponent } from 'vue'
import { ElMessageBox, type TableInstance, type UploadFile } from 'element-plus'
import { Picture, Delete } from '@element-plus/icons-vue'
import { api } from '@/api'
import AddDelayed from './AddDelayed.vue'
import type { Shift } from '@/api/resources/chatters/model'
import type { TableColumnCtx } from 'element-plus/es/components/index.mjs'

const props = defineProps<{
  chatter: Chatter
}>()

const emit = defineEmits(['change'])

const tableRef = ref<TableInstance>()

const expandRow = (shiftId: number) => {
  setTimeout(() => {
    const row = props.chatter.assigned_shifts.find((shift) => shift.id === shiftId)!
    tableRef.value?.toggleRowExpansion(row, true)
  }, 300)
}

const getPhotoUrl = (name: string) => {
  return `${import.meta.env.VITE_API_BASE_URL}/incomes/screenshots/${openedScreenshot.value}${name}?${Date.now()}`
}

const openedScreenshot = ref<number | null>(null)

const income = ref<Partial<IncomeToCreate>>({
  in_progress: undefined,
  unread: undefined,
  comment: '',
  model: '',
  amount: undefined,
  is_delayed: false,
  success_poor_detects: undefined,
  failed_poor_detects: undefined,
})

const addIncome = async (shiftId: number, newIncome: IncomeToCreate, screenshots: UploadFile[]) => {
  openedDelayed.value = null
  const incomeId = await api.incomes.create({
    ...newIncome,
    shift_id: shiftId,
  })

  // If there's a photo, upload it
  if (screenshots.length > 0) {
    const formData = new FormData()
    screenshots.forEach((screenshot) => {
      if (screenshot.raw) {
        formData.append('files', screenshot.raw)
      }
    })
    await api.addIncomeImg({
      payload: formData,
      urlParams: {
        incomeId,
      },
    })
  }
  income.value = {
    in_progress: undefined,
    unread: undefined,
    comment: '',
    model: '',
    amount: undefined,
    is_delayed: false,
    success_poor_detects: undefined,
    failed_poor_detects: undefined,
  }
  emit('change')
  expandRow(shiftId)
}

const deleteIncome = async (incomeId: number, shiftId: number) => {
  ElMessageBox.confirm('Удалить запись?', {
    confirmButtonText: 'Да',
    cancelButtonText: 'Отмена',
    type: 'warning',
  })
    .then(async () => {
      await api.incomes.delete(incomeId)
      emit('change')
      expandRow(shiftId)
    })
    .catch(() => {
      console.log('Отмена удаления')
    })
}

const openedDelayed = ref<Income | null>(null)

const getShiftIncomes = (shift: Shift) => {
  const incomesByModel = shift.incomes.reduce(
    (acc: Record<string, Income[]>, income: Income) => {
      if (!acc[income.model]) {
        acc[income.model] = []
      }
      acc[income.model].push(income)
      return acc
    },
    {} as Record<string, Income[]>,
  )
  return Object.entries(incomesByModel).map(([model, incomes]) => ({ model, incomes }))
}

const getSummaries = (param: { columns: TableColumnCtx<Income>[]; data: Income[] }) => {
  const button = h(
    resolveComponent('el-button'),
    {
      type: 'primary',
      onClick: () => (openedDelayed.value = param.data[0]),
    },
    () => '+ долет',
  )

  const totals = param.data.reduce(
    (acc, row) => {
      if (row.is_delayed) {
        acc.delayed += row.amount
      }
      acc.general += row.amount

      return acc
    },
    {
      general: 0,
      delayed: 0,
    },
  )
  return ['Итого:', totals.general, 'Долетами:', totals.delayed, , , button]
}
</script>

<style scoped>
.shifts {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
</style>
